import React, {useContext, useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlined";
import Loading from "../../components/Common/Loading";
import {SnackBarContext} from "../../snackBarProvider";
import {
    useAddDepotPointsTOHMutation,
    useAwardDepotLeagueTOHMutation,
    useGetAroundDepotEventsTOHQuery,
    useGetDepotLeagueRecalculationStatusTOHQuery,
    useGetDepotLeagueTopTOHQuery,
    useGetDepotLeaguesTOHQuery,
    useGetDepotProgressTOHQuery,
    useScheduleDepotLeagueRecalculationTOHMutation,
    useSetDepotLeagueScoresTOHMutation,
} from "../../core/api/toh-clans-api";
import {
    CLEAR_ACTIONS,
    ClearProgressDialog,
    ClanLeagueProgressCard,
    eventStatus,
    flattenAroundEvents,
    formatDateTime,
    LeagueOverviewCard,
    LeagueRecalculationCard,
} from "../BossEvents/toh";


function ActiveDepotEventsCard({
    events,
    isLoading,
    isError,
    error,
    onRefresh,
    isFetching,
    defaultClanId,
    onSelectEvent,
    selectedEventId,
}) {
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const [clearTarget, setClearTarget] = useState(null);

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            addAlert({text: 'Copied to clipboard', type: 'success'});
        } catch {
            addAlert({text: 'Failed to copy', type: 'error'});
        }
    };

    return (
        <Card>
            <CardContent>
                <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                    <Typography variant="h6">Depot Events</Typography>
                    <Tooltip title="Reload events">
                        <span>
                            <IconButton size="small" onClick={onRefresh} disabled={isFetching}>
                                <RefreshIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>

                <Loading isLoading={isLoading} isError={isError} error={error}>
                    {Array.isArray(events) && events.length > 0 ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Event ID</TableCell>
                                    <TableCell>Start</TableCell>
                                    <TableCell>End</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell align="right"></TableCell>
                                    <TableCell align="center">Depot Progress</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events.map((event) => {
                                    const status = eventStatus(event);
                                    const isSelected = selectedEventId === event.id;
                                    return (
                                        <TableRow
                                            key={event.id}
                                            hover
                                            selected={isSelected}
                                            sx={{cursor: 'pointer'}}
                                            onClick={() => onSelectEvent(event.id)}
                                        >
                                            <TableCell>
                                                <Chip
                                                    icon={status.label === 'Active' ? <CheckCircleIcon/> : <ScheduleIcon/>}
                                                    label={status.label}
                                                    color={status.color}
                                                    size="small"
                                                    variant={status.label === 'Active' ? 'filled' : 'outlined'}
                                                />
                                            </TableCell>
                                            <TableCell sx={{}}>
                                                {event.id}
                                            </TableCell>
                                            <TableCell>{formatDateTime(event.distribution?.start)}</TableCell>
                                            <TableCell>{formatDateTime(event.distribution?.end)}</TableCell>
                                            <TableCell>{formatDateTime(event.createdAt)}</TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} sx={{justifyContent: 'flex-end'}}>
                                                    <Tooltip title="Copy event ID">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCopy(event.id);
                                                            }}
                                                        >
                                                            <ContentCopyIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Button
                                                        size="small"
                                                        variant={isSelected ? "contained" : "outlined"}
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onSelectEvent(event.id);
                                                        }}
                                                    >
                                                        {isSelected ? 'Selected' : 'Select'}
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} sx={{justifyContent: 'center'}}>
                                                    <Tooltip title={CLEAR_ACTIONS.depot.tooltip}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setClearTarget({type: 'depot', eventId: event.id});
                                                            }}
                                                        >
                                                            Clear
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title={CLEAR_ACTIONS.depotAll.tooltip}>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setClearTarget({type: 'depotAll', eventId: event.id});
                                                            }}
                                                        >
                                                            Clear All
                                                        </Button>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    ) : (
                        <Typography sx={{color: 'text.secondary', py: 2}}>
                            No events
                        </Typography>
                    )}
                </Loading>
            </CardContent>

            <ClearProgressDialog
                target={clearTarget}
                defaultClanId={defaultClanId}
                onClose={() => setClearTarget(null)}
            />
        </Card>
    );
}


function AddPointsCard({eventId, deviceId, defaultClanId}) {
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const [addPoints, addPointsResponse] = useAddDepotPointsTOHMutation();

    const [userId, setUserId] = useState(deviceId || '');
    const [userIdTouched, setUserIdTouched] = useState(false);
    const [clanId, setClanId] = useState(defaultClanId || '');
    const [clanIdTouched, setClanIdTouched] = useState(false);
    const [points, setPoints] = useState('');
    const [reason, setReason] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        if (!userIdTouched) setUserId(deviceId || '');
    }, [deviceId, userIdTouched]);

    useEffect(() => {
        if (!clanIdTouched) setClanId(defaultClanId || '');
    }, [defaultClanId, clanIdTouched]);

    const pointsNumber = parseInt(points);
    const trimmedUserId = userId?.trim() ?? '';
    const isValid = (
        eventId?.trim()
        && clanId?.trim()
        && points !== '' && Number.isFinite(pointsNumber)
        && reason?.trim()
    );

    const handleSubmit = async () => {
        const body = {
            eventId: eventId.trim(),
            clanId: clanId.trim(),
            points: pointsNumber,
            reason: reason.trim(),
        };
        if (trimmedUserId) body.userId = trimmedUserId;
        const response = await addPoints(body);
        setConfirmOpen(false);
        if (response.hasOwnProperty('error')) {
            const message = response.error?.data?.message || response.error?.data || 'Failed to add points';
            addAlert({text: typeof message === 'string' ? message : JSON.stringify(message), type: 'error'});
        } else {
            const target = trimmedUserId || 'clan leader';
            const signed = pointsNumber > 0 ? `+${pointsNumber}` : String(pointsNumber);
            addAlert({text: `Points ${signed} applied to ${target} (clan ${clanId})`, type: 'success'});
            setPoints('');
            setReason('');
        }
    };

    return (
        <Card>
            <CardContent>
                <Stack direction="row" spacing={1} sx={{alignItems: 'center', mb: 2}}>
                    <Typography variant="h6">Add Points</Typography>
                </Stack>
                <Typography variant="body2" sx={{color: 'text.secondary', mb: 2}}>
                    Manually adds donation score points to a clan, bypassing the donate flow.
                    If User ID is empty, points are credited to the clan leader.
                    Negative values are allowed for corrections; carriageBuildCount does not go below 0.
                </Typography>

                {!eventId && (
                    <Alert severity="warning" sx={{mb: 2}}>
                        Select an event from the table above
                    </Alert>
                )}

                <Stack spacing={2}>
                    <TextField
                        label="Event ID" size="small" fullWidth required
                        value={eventId || ''}
                        InputProps={{readOnly: true}}
                        helperText="Set by clicking a row in the events table"
                    />
                    <TextField
                        label="User ID" size="small" fullWidth
                        value={userId}
                        onChange={(e) => { setUserId(e.target.value); setUserIdTouched(true); }}
                        helperText="Optional. Empty → credit clan leader. Auto-filled from current player"
                    />
                    <TextField
                        label="Clan ID (UUID)" size="small" fullWidth required
                        value={clanId}
                        onChange={(e) => { setClanId(e.target.value); setClanIdTouched(true); }}
                        helperText="Auto-filled from player's clan; override if needed"
                    />
                    <TextField
                        label="Points" size="small" fullWidth required type="number"
                        value={points} onChange={(e) => setPoints(e.target.value)}
                        helperText="Donation score units. Can be negative (correction). Clan total clamped at ≥ 0"
                    />
                    <TextField
                        label="Reason" size="small" fullWidth required multiline minRows={2}
                        value={reason} onChange={(e) => setReason(e.target.value)}
                        helperText="Goes to the audit log"
                    />
                </Stack>

                <Box sx={{mt: 2}}>
                    <LoadingButton
                        loading={addPointsResponse.isLoading}
                        variant="contained" color="primary"
                        startIcon={<AddCircleOutlineIcon/>}
                        disabled={!isValid}
                        onClick={() => setConfirmOpen(true)}
                    >
                        Add Points
                    </LoadingButton>
                </Box>
            </CardContent>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>Confirm Add Points</Typography>
                    <Typography>
                        Apply <strong>{pointsNumber}</strong> points to{' '}
                        <strong>{trimmedUserId || 'clan leader'}</strong>
                    </Typography>
                    <Typography>clan</Typography>
                    <Typography sx={{}}>{clanId}</Typography>
                    <Typography>on event</Typography>
                    <Typography sx={{}}>{eventId}</Typography>
                    <Typography sx={{mt: 1}}>Reason: <em>{reason}</em></Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <LoadingButton
                        loading={addPointsResponse.isLoading}
                        variant="contained" color="error"
                        onClick={handleSubmit}
                    >
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Card>
    );
}


const DEPOT_RECALC_INVALIDATE_TAGS = [
    'TohDepotLeagues',
    'TohDepotLeagueTop',
    'TohDepotProgress',
];


function DepotEventsTOH({deviceId = '', defaultClanId = ''}) {
    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
    } = useGetAroundDepotEventsTOHQuery();

    const events = flattenAroundEvents(data);
    const [selectedEventId, setSelectedEventId] = useState('');

    useEffect(() => {
        if (!selectedEventId && events.length > 0) {
            const active = events.find((e) => eventStatus(e).label === 'Active');
            setSelectedEventId((active || events[0]).id);
        }
    }, [events, selectedEventId]);

    return (
        <Box>
            <Box sx={{mb: 2}}>
                <ActiveDepotEventsCard
                    events={events}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    isError={isError}
                    error={error}
                    onRefresh={refetch}
                    defaultClanId={defaultClanId}
                    onSelectEvent={setSelectedEventId}
                    selectedEventId={selectedEventId}
                />
            </Box>

            <Box sx={{mb: 2}}>
                <ClanLeagueProgressCard
                    eventId={selectedEventId}
                    defaultClanId={defaultClanId}
                    pointsLabel="Carriage Builds"
                    useProgressQuery={useGetDepotProgressTOHQuery}
                />
            </Box>

            <Box sx={{mb: 2}}>
                <AddPointsCard
                    eventId={selectedEventId}
                    deviceId={deviceId}
                    defaultClanId={defaultClanId}
                />
            </Box>

            <Box sx={{mb: 2}}>
                <LeagueOverviewCard
                    eventId={selectedEventId}
                    pointsLabel="Carriage Builds"
                    useLeaguesQuery={useGetDepotLeaguesTOHQuery}
                    useLeagueTopQuery={useGetDepotLeagueTopTOHQuery}
                    useAwardMutation={useAwardDepotLeagueTOHMutation}
                    useSetScoresMutation={useSetDepotLeagueScoresTOHMutation}
                />
            </Box>

            <LeagueRecalculationCard
                events={events}
                defaultTargetEventId={selectedEventId}
                useStatusQuery={useGetDepotLeagueRecalculationStatusTOHQuery}
                useScheduleMutation={useScheduleDepotLeagueRecalculationTOHMutation}
                invalidateTags={DEPOT_RECALC_INVALIDATE_TAGS}
            />
        </Box>
    );
}


export default DepotEventsTOH;
